import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  List,
  Paper,
  Chip,
  Avatar,
  Pagination,
  Collapse,
  TextField,
  Button,
  InputAdornment,
  Divider,
  MenuItem
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import RedeemRoundedIcon from '@mui/icons-material/RedeemRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import Loader from './Loader';
import api from '../../../services/api';
import { formatInTimeZone } from 'date-fns-tz';
import { ptBR } from 'date-fns/locale';

const BRAZIL_TZ = 'America/Sao_Paulo';
const EVENTS_PER_PAGE = 100;

const FILTERS_INITIAL_STATE = {
  type: 'all',
  query: '',
  from: '',
  to: ''
};

const EVENT_TYPE_OPTIONS = [
  { value: 'all', label: 'Todos os tipos' },
  { value: 'checkin', label: 'Check-in' },
  { value: 'redemption', label: 'Brinde resgatado' }
];

const EVENT_META = {
  checkin: {
    label: 'Check-in realizado',
    chip: 'CHECK-IN',
    icon: LocationOnRoundedIcon,
    tone: {
      main: '#16a34a',
      soft: 'rgba(22, 163, 74, 0.12)',
      chipBg: '#dcfce7',
      chipText: '#14532d',
      line: 'rgba(22, 163, 74, 0.35)'
    }
  },
  redemption: {
    label: 'Brinde resgatado',
    chip: 'BRINDE',
    icon: RedeemRoundedIcon,
    tone: {
      main: '#475569',
      soft: 'rgba(71, 85, 105, 0.12)',
      chipBg: '#e2e8f0',
      chipText: '#334155',
      line: 'rgba(100, 116, 139, 0.25)'
    }
  }
};

const FALLBACK_EVENT_META = {
  label: 'Evento registrado',
  chip: 'EVENTO',
  icon: InfoOutlinedIcon,
  tone: {
    main: '#64748b',
    soft: 'rgba(100, 116, 139, 0.12)',
    chipBg: '#e2e8f0',
    chipText: '#475569',
    line: 'rgba(148, 163, 184, 0.3)'
  }
};

const getEventMeta = (type) => EVENT_META[type] || FALLBACK_EVENT_META;

const cleanPhone = (phone) => {
  if (!phone) return '';
  return String(phone).replace(/\D/g, '');
};

const formatTimeLabel = (value) =>
  formatInTimeZone(value, BRAZIL_TZ, 'HH:mm');

const getDayKey = (value) =>
  formatInTimeZone(value, BRAZIL_TZ, 'yyyy-MM-dd');

const getDayLabel = (value) =>
  formatInTimeZone(value, BRAZIL_TZ, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR });

const parseCoordinate = (value) => {
  if (value === null || value === undefined) return null;
  const numeric = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(numeric) ? numeric : null;
};

const formatLocationLabel = ({ lat, long, confirmed }) => {
  const parsedLat = parseCoordinate(lat);
  const parsedLong = parseCoordinate(long);

  if (parsedLat !== null && parsedLong !== null) {
    return `Lat: ${parsedLat.toFixed(5)} | Long: ${parsedLong.toFixed(5)}`;
  }

  if (confirmed === true) return 'Confirmada';
  if (confirmed === false) return 'Não confirmada';
  return 'Não informada';
};

const formatPhone = (phone) => {
  const clean = cleanPhone(phone);
  if (!clean) return '';

  let digits = clean;
  if (digits.startsWith('55')) digits = digits.slice(2);
  if (digits.length === 10) {
    digits = `${digits.slice(0, 2)}9${digits.slice(2)}`;
  }
  if (digits.length !== 11) return digits;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const HistoricoTab = () => {
  const [clients, setClients] = useState({});
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(FILTERS_INITIAL_STATE);
  const [expandedEvents, setExpandedEvents] = useState(() => new Set());

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [clientsRes, checkinsRes, redemptionsRes] = await Promise.all([
          api.get('/fidelidade/clientes'),
          api.get('/fidelidade/checkins'),
          api.get('/fidelidade/brindes-clientes')
        ]);

        const clientsMap = {};
        clientsRes.data.forEach(client => {
          const cleanedPhone = cleanPhone(client.telefone);
          clientsMap[cleanedPhone] = client;
        });
        setClients(clientsMap);

        const allEvents = [];

        (checkinsRes.data || []).forEach((checkin, index) => {
          const phone = checkin?.cliente_telefone || checkin?.telefone;
          const cleanedPhone = cleanPhone(phone);
          const isoDate = checkin?.checkin_iniciado_em;
          const jsDate = new Date(isoDate);

          if (!phone || Number.isNaN(jsDate.getTime())) return;

          allEvents.push({
            id: `checkin-${cleanedPhone || 'unknown'}-${jsDate.getTime()}-${index}`,
            type: 'checkin',
            timestamp: jsDate.getTime(),
            dayKey: getDayKey(jsDate),
            dayLabel: getDayLabel(jsDate),
            timeLabel: formatTimeLabel(jsDate),
            clientPhone: phone,
            location: formatLocationLabel({
              lat: checkin?.lat,
              long: checkin?.long,
              confirmed: checkin?.localizacao_confirmada
            })
          });
        });

        (redemptionsRes.data || []).forEach((redemption, index) => {
          const phone = redemption?.cliente_telefone;
          const cleanedPhone = cleanPhone(phone);
          const isoDate = redemption?.resgatado_em;
          const jsDate = new Date(isoDate);

          if (!phone || Number.isNaN(jsDate.getTime())) return;

          allEvents.push({
            id: `redemption-${cleanedPhone || 'unknown'}-${jsDate.getTime()}-${index}`,
            type: 'redemption',
            timestamp: jsDate.getTime(),
            dayKey: getDayKey(jsDate),
            dayLabel: getDayLabel(jsDate),
            timeLabel: formatTimeLabel(jsDate),
            clientPhone: phone,
            giftName: redemption?.brinde_nome || redemption?.brinde?.nome || 'Brinde não identificado'
          });
        });

        allEvents.sort((a, b) => b.timestamp - a.timestamp);
        setEvents(allEvents);
        setPage(1);
      } catch (error) {
        console.error('Error fetching history data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getClientName = (phone) => {
    const cleanedPhone = cleanPhone(phone);
    return clients[cleanedPhone]?.nome || 'Cliente não encontrado';
  };

  const filteredEvents = useMemo(() => {
    if (!events.length) return [];

    const query = filters.query.trim().toLowerCase();
    const queryDigits = cleanPhone(query);

    return events.filter((event) => {
      if (filters.type !== 'all' && event.type !== filters.type) return false;
      if (filters.from && event.dayKey < filters.from) return false;
      if (filters.to && event.dayKey > filters.to) return false;

      if (query) {
        const clientName = (clients[cleanPhone(event.clientPhone)]?.nome || '').toLowerCase();
        const phoneDigits = cleanPhone(event.clientPhone);
        const matchesName = clientName.includes(query);
        const matchesPhone = queryDigits ? phoneDigits.includes(queryDigits) : false;

        if (!matchesName && !matchesPhone) return false;
      }

      return true;
    });
  }, [events, filters.type, filters.query, filters.from, filters.to, clients]);

  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / EVENTS_PER_PAGE));

  useEffect(() => {
    setPage(1);
  }, [filters.type, filters.query, filters.from, filters.to]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedEvents = useMemo(() => {
    const startIndex = (page - 1) * EVENTS_PER_PAGE;
    return filteredEvents.slice(startIndex, startIndex + EVENTS_PER_PAGE);
  }, [filteredEvents, page]);

  const groupedEvents = useMemo(() => {
    if (paginatedEvents.length === 0) return [];

    const groups = [];
    const tracker = new Map();

    paginatedEvents.forEach(event => {
      if (!tracker.has(event.dayKey)) {
        const group = { dayKey: event.dayKey, label: event.dayLabel, events: [] };
        tracker.set(event.dayKey, group);
        groups.push(group);
      }

      tracker.get(event.dayKey).events.push(event);
    });

    return groups;
  }, [paginatedEvents]);

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleClearFilters = () => {
    setFilters({ ...FILTERS_INITIAL_STATE });
    setPage(1);
  };

  const handleToggleDetails = (eventId) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const hasActiveFilters = filters.type !== 'all'
    || filters.query.trim().length > 0
    || filters.from
    || filters.to;

  const rangeStart = filteredEvents.length === 0 ? 0 : (page - 1) * EVENTS_PER_PAGE + 1;
  const rangeEnd = Math.min(filteredEvents.length, page * EVENTS_PER_PAGE);

  if (loading) return <Loader />;

  return (
    <Box>
      <Box mb={2.5}>
        <Typography variant="h5" fontWeight={700}>
          Histórico
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Registro cronológico das atividades do sistema.
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.9) 0%, rgba(255, 255, 255, 0.98) 100%)'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mb: 1.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterAltRoundedIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight={600}>
              Filtros (opcional)
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {filteredEvents.length} evento(s)
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'center'
          }}
        >
          <TextField
            label="Buscar cliente"
            size="small"
            placeholder="Nome ou telefone"
            value={filters.query}
            onChange={handleFilterChange('query')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ flex: '1 1 240px' }}
          />
          <TextField
            select
            label="Tipo de evento"
            size="small"
            value={filters.type}
            onChange={handleFilterChange('type')}
            sx={{ flex: '1 1 200px', minWidth: 200 }}
          >
            {EVENT_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="De"
            type="date"
            size="small"
            value={filters.from}
            onChange={handleFilterChange('from')}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: '1 1 160px', minWidth: 160 }}
          />
          <TextField
            label="Até"
            type="date"
            size="small"
            value={filters.to}
            onChange={handleFilterChange('to')}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: '1 1 160px', minWidth: 160 }}
          />
          <Button
            variant="text"
            size="small"
            onClick={handleClearFilters}
            disabled={!hasActiveFilters}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              alignSelf: { xs: 'stretch', sm: 'center' }
            }}
          >
            Limpar filtros
          </Button>
        </Box>
      </Paper>

      {groupedEvents.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          {events.length === 0
            ? 'Nenhum evento encontrado.'
            : 'Nenhum evento encontrado para os filtros selecionados.'}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {groupedEvents.map(({ dayKey, label, events: dayEvents }) => (
            <motion.div
              key={dayKey}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  overflow: 'hidden',
                  backgroundColor: 'grey.50'
                }}
              >
                <Box
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.5, sm: 2 },
                    backgroundColor: 'grey.100',
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap'
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
                    {label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dayEvents.length} evento(s)
                  </Typography>
                </Box>

                <List
                  component="div"
                  disablePadding
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 1.5, sm: 2 },
                    px: { xs: 1.5, sm: 3 },
                    py: { xs: 2, sm: 2.5 }
                  }}
                >
                  <AnimatePresence initial={false}>
                    {dayEvents.map((event, index) => {
                      const meta = getEventMeta(event.type);
                      const EventIcon = meta.icon;
                      const clientName = getClientName(event.clientPhone);
                      const detailsId = `event-details-${event.id}`;
                      const isExpanded = expandedEvents.has(event.id);
                      const isLast = index === dayEvents.length - 1;

                      const detailItems = [
                        { label: 'Telefone', value: formatPhone(event.clientPhone) || 'Não informado' }
                      ];

                      if (event.type === 'checkin') {
                        detailItems.push({ label: 'Localização', value: event.location || 'Não informada' });
                      } else if (event.type === 'redemption') {
                        detailItems.push({ label: 'Brinde', value: event.giftName || 'Brinde não identificado' });
                      }

                      return (
                        <motion.div
                          key={event.id}
                          layout
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.25, delay: index * 0.03 }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'stretch',
                              gap: { xs: 1.5, sm: 2.5 }
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: { xs: 40, sm: 48 },
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                pt: 0.5,
                                pb: isLast ? 0 : { xs: 2, sm: 3 }
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  bgcolor: meta.tone.soft,
                                  color: meta.tone.main,
                                  border: '1px solid',
                                  borderColor: meta.tone.main,
                                  boxShadow: '0 6px 14px rgba(15, 23, 42, 0.08)'
                                }}
                              >
                                <EventIcon fontSize="small" />
                              </Avatar>
                              {!isLast && (
                                <Box
                                  sx={{
                                    mt: 1.5,
                                    flexGrow: 1,
                                    width: 2,
                                    borderRadius: 1,
                                    background: `linear-gradient(180deg, ${meta.tone.line}, rgba(148, 163, 184, 0.12))`
                                  }}
                                />
                              )}
                            </Box>

                            <Paper
                              elevation={0}
                              sx={{
                                flexGrow: 1,
                                borderRadius: 3,
                                px: { xs: 2, sm: 2.5 },
                                py: { xs: 1.75, sm: 2 },
                                border: '1px solid',
                                borderColor: 'grey.200',
                                backgroundColor: 'common.white',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                transition: 'box-shadow 0.2s ease',
                                '&:hover': {
                                  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)'
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  gap: 1.25
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={600}
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: 0.6,
                                    color: 'text.primary'
                                  }}
                                >
                                  <Box component="span">{clientName}</Box>
                                  <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    - {meta.label}
                                  </Box>
                                  <Box component="span" sx={{ color: 'text.secondary' }}>
                                    | {event.timeLabel}
                                  </Box>
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip
                                    label={meta.chip}
                                    size="small"
                                    sx={{
                                      bgcolor: meta.tone.chipBg,
                                      color: meta.tone.chipText,
                                      fontWeight: 700,
                                      letterSpacing: 0.6,
                                      textTransform: 'uppercase'
                                    }}
                                  />
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => handleToggleDetails(event.id)}
                                    endIcon={
                                      <ExpandMoreRoundedIcon
                                        sx={{
                                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                          transition: 'transform 0.2s ease'
                                        }}
                                      />
                                    }
                                    aria-expanded={isExpanded}
                                    aria-controls={detailsId}
                                    sx={{
                                      textTransform: 'none',
                                      fontWeight: 600,
                                      color: 'text.secondary'
                                    }}
                                  >
                                    {isExpanded ? 'Ocultar' : 'Detalhes'}
                                  </Button>
                                </Box>
                              </Box>

                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography
                                  variant="overline"
                                  color="text.secondary"
                                  sx={{ letterSpacing: 1.2, fontWeight: 600 }}
                                >
                                  Detalhes do evento
                                </Typography>
                                <Box
                                  id={detailsId}
                                  sx={{
                                    mt: 1,
                                    display: 'grid',
                                    gridTemplateColumns: {
                                      xs: 'repeat(1, minmax(0, 1fr))',
                                      sm: 'repeat(2, minmax(0, 1fr))'
                                    },
                                    gap: { xs: 1.25, sm: 1.75 }
                                  }}
                                >
                                  {detailItems.map(({ label, value }) => (
                                    <Box
                                      key={label}
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.4
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ letterSpacing: 0.6, fontWeight: 600 }}
                                      >
                                        {label}
                                      </Typography>
                                      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                        {value || 'Não informado'}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Collapse>
                            </Paper>
                          </Box>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </List>
              </Paper>
            </motion.div>
          ))}
        </Box>
      )}

      {filteredEvents.length > 0 && (
        <Box mt={2} display="flex" flexDirection="column" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary">
            Exibindo {rangeStart}-{rangeEnd} de {filteredEvents.length} eventos
          </Typography>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default HistoricoTab;
